/**
 * Map-display buckets for a deal's status: Early, Negotiating, Committed,
 * Ours, Dead — the stages a landman reads tract colours by.
 *
 * SOURCE. Open / closed / dead is not decided here: it comes from
 * utils/dealStatusVocab (the app's single status vocabulary — isDeadStatus,
 * isClosedStatus). de-hub migration 100_deal_status_lifecycle puts the same
 * open / closed / dead split in data (pick_lists.category), but the code that
 * serves it to the client is only on de-hub's reports branch, not master, so
 * the app cannot read it yet.
 *
 * KNOWN GAP: the split of OPEN statuses into Early / Negotiating / Committed
 * exists nowhere as data (category is only open / closed / dead), so it is
 * the small mapping below. Move it to data — e.g. a finer pick_lists
 * category — when one exists. The display names are source literals for the
 * same reason.
 */
import {
  isClosedStatus,
  isDeadStatus,
  normalizeStatus,
} from "./dealStatusVocab";

/** Buckets in lifecycle order (index = rank). */
export const TRACT_BUCKETS = [
  { key: "early", label: "Early" },
  { key: "negotiating", label: "Negotiating" },
  { key: "committed", label: "Committed" },
  { key: "ours", label: "Ours" },
  { key: "dead", label: "Dead" },
];

const RANK = Object.fromEntries(TRACT_BUCKETS.map((b, i) => [b.key, i]));

// Dead statuses migration 100 lists that the app vocabulary doesn't seed.
const EXTRA_DEAD = new Set(["TITLE FAILURE", "TITLE FAILED", "UNLOCATABLE"]);
// Won statuses from the landman's list that aren't in the canonical
// vocabulary (CLOSED is); kept so a legacy row still reads as ours.
const EXTRA_OURS = new Set(["TITLE CONFIRMED", "RECORDED"]);
const NEGOTIATING = new Set(["NEGOTIATING", "WAITING ON WARWICK"]);

/**
 * @param {string|null|undefined} status  raw deal_status
 * @returns {"early"|"negotiating"|"committed"|"ours"|"dead"}
 *   Unknown and blank statuses are Early — an un-triaged deal is live work.
 */
export const statusBucket = (status) => {
  const s = normalizeStatus(status);
  if (isDeadStatus(s) || EXTRA_DEAD.has(s)) return "dead";
  if (isClosedStatus(s) || EXTRA_OURS.has(s)) return "ours";
  if (s.startsWith("COMMITTED")) return "committed";
  if (NEGOTIATING.has(s)) return "negotiating";
  return "early";
};

/**
 * The most advanced LIVE bucket among several deals on one shape; "dead"
 * only when every deal there is dead.
 *
 * @param {string[]} buckets
 * @returns {string|null} null for an empty list
 */
export const mostAdvancedBucket = (buckets) => {
  let best = null;
  for (const b of Array.isArray(buckets) ? buckets : []) {
    if (!(b in RANK)) continue;
    if (best === null) {
      best = b;
      continue;
    }
    if (best === "dead") best = b;
    else if (b !== "dead" && RANK[b] > RANK[best]) best = b;
  }
  return best;
};
